<template>
  <div ref="rootRef" :class="mainClasses">
    <button
      ref="triggerRef"
      id="sort-options-trigger"
      type="button"
      :class="triggerButtonClasses"
      aria-haspopup="listbox"
      :aria-expanded="open"
      aria-controls="sort-options-list"
      @click="toggleOpen(false)"
      @keydown="onTriggerKeydown"
    >
      {{ selectedLabel }}
    </button>

    <div
      v-if="open"
      id="sort-options-list"
      role="listbox"
      aria-labelledby="sort-options-trigger"
      :class="[listboxClasses, shouldScroll ? 'overflow-y-auto [scrollbar-gutter:stable]' : 'overflow-y-hidden']"
      :style="panelStyle"
    >
      <div
        v-for="(opt, idx) in props.options"
        :key="String(opt.value)"
        :id="optionId(idx)"
        role="option"
        tabindex="0"
        :aria-selected="String(opt.value) === selectedStr"
        :class="optionClasses(idx)"
        @focus="focusedIndex = idx"
        @keydown="onOptionKeydown($event, idx)"
        @mousedown.prevent="pickOption(opt.value)"
        @mouseenter="focusedIndex = idx"
      >
        {{ opt.label }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue"
import type { SortOption } from "~~/shared/types/common"

const props = defineProps<{
  options: SortOption[]
  modelValue?: string | number | null
}>()

const emit = defineEmits(["update:modelValue", "sort-change"])

const rootRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLButtonElement | null>(null)
const open = ref(false)
const focusedIndex = ref(-1)
const selectedStr = ref("")
const lastValidModelStr = ref<string | null>(null)
const panelWidth = ref<number | null>(null)

const mainClasses = computed(() =>
  "relative hidden w-full min-w-0 items-center justify-end rounded-[0.4286rem] border border-gray-300 after:pointer-events-none after:absolute after:right-[1.125em] after:top-1/2 after:mt-[-0.4375em] after:h-[0.625em] after:w-[0.625em] after:-rotate-45 after:border-[0.2143rem] after:border-r-0 after:border-t-0 after:content-[''] mobile-dropdown lg:flex lg:w-auto lg:min-w-[15rem]"
)

const triggerButtonClasses = computed(() =>
  "w-full min-w-0 cursor-pointer appearance-none border-0 bg-transparent py-[0.4286rem] pl-4 pr-10 text-left font-inherit text-[1rem] text-ash-200 focus:outline-auto"
)

const listboxClasses = computed(() =>
  "absolute left-0 top-full z-10 box-border max-h-60 w-full min-w-0 max-w-full overflow-x-hidden overscroll-contain rounded-[0.4286rem] border border-gray-300 bg-white shadow-lg"
)

const optionClasses = (idx: number) =>
  [
    "box-border w-full min-w-0 cursor-pointer break-words px-4 py-[0.4286rem] text-left text-[1rem] text-ash-200 hover:bg-gray-300 focus:outline-auto focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400",
    idx === focusedIndex.value ? "bg-gray-300" : ""
  ].join(" ")

const selectedLabel = computed(() => {
  const opt = props.options.find(o => String(o.value) === selectedStr.value)
  return opt?.label ?? "Select option"
})

const shouldScroll = computed(() => props.options.length > 7)
const panelStyle = computed(() =>
  panelWidth.value !== null ? { width: `${panelWidth.value}px` } : undefined
)

function syncPanelWidth() {
  panelWidth.value = triggerRef.value?.offsetWidth ?? null
}

function optionId(idx: number) {
  return `sort-options-opt-${idx}`
}

function focusOptionIndex(idx: number) {
  if (idx < 0 || idx >= props.options.length) return
  focusedIndex.value = idx
  nextTick(() => {
    const el = document.getElementById(optionId(idx)) as HTMLElement | null
    el?.focus()
    el?.scrollIntoView({ block: "nearest" })
  })
}

/** Wrap at ends: last + Down → first, first + Up → last (until Esc closes the list). */
function moveIndexCircular(idx: number, delta: number) {
  const len = props.options.length
  if (len === 0) return 0
  return (idx + delta + len) % len
}

function syncSelectedFromModel() {
  if (!props.options.length) {
    selectedStr.value = ""
    return
  }

  const modelValueFromParent = props.modelValue
  if (modelValueFromParent !== undefined && modelValueFromParent !== null && modelValueFromParent !== "") {
    const normalizedModelValue = String(modelValueFromParent)
    selectedStr.value = normalizedModelValue
    lastValidModelStr.value = normalizedModelValue
    return
  }

  if (lastValidModelStr.value !== null) {
    const optionStillExists = props.options.some(
      option => String(option.value) === lastValidModelStr.value
    )
    if (optionStillExists) {
      selectedStr.value = lastValidModelStr.value
      return
    }
  }

  const firstOption = props.options[0]
  selectedStr.value = firstOption !== undefined ? String(firstOption.value) : ""
}


watch(
  () => [props.modelValue, props.options] as const,
  () => {
    syncSelectedFromModel()
  },
  { deep: true, immediate: true }
)

function pickOption(val: string | number) {
  const selectedOption = props.options.find(opt => String(opt.value) === String(val))
  if (!selectedOption) return
  const canonical = String(selectedOption.value)
  selectedStr.value = canonical
  lastValidModelStr.value = canonical
  open.value = false
  focusedIndex.value = -1
  emit("update:modelValue", selectedOption.value)
  emit("sort-change", {
    sort: selectedOption.sortp
  })
  nextTick(() => triggerRef.value?.focus())
}

function currentOptionIndex() {
  const i = props.options.findIndex(o => String(o.value) === selectedStr.value)
  return i >= 0 ? i : 0
}

function closeList(focusTrigger = false) {
  open.value = false
  focusedIndex.value = -1
  if (focusTrigger) {
    nextTick(() => triggerRef.value?.focus())
  }
}

function toggleOpen(focusFirstOption = false) {
  open.value = !open.value
  focusedIndex.value = open.value ? currentOptionIndex() : -1
  if (open.value) {
    nextTick(() => {
      syncPanelWidth()
      if (focusFirstOption) {
        focusOptionIndex(focusedIndex.value)
      }
    })
  }
}

function onTriggerKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    if (open.value) {
      e.preventDefault()
      e.stopPropagation()
      closeList(true)
    }
    return
  }

  if (e.key === "Tab" && open.value && props.options.length) {
    e.preventDefault()
    if (e.shiftKey) {
      focusOptionIndex(props.options.length - 1)
    } else {
      focusOptionIndex(0)
    }
    return
  }

  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault()
    if (!open.value) {
      toggleOpen(true)
      return
    }
    const delta = e.key === "ArrowDown" ? 1 : -1
    const cur =
      focusedIndex.value >= 0 && focusedIndex.value < props.options.length
        ? focusedIndex.value
        : currentOptionIndex()
    focusOptionIndex(moveIndexCircular(cur, delta))
    return
  }

  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault()
    if (!open.value) {
      toggleOpen(true)
      return
    }
    const idx = focusedIndex.value
    const opt = idx >= 0 && idx < props.options.length ? props.options[idx] : undefined
    if (opt !== undefined) {
      pickOption(opt.value)
    }
  }
}

function onOptionKeydown(e: KeyboardEvent, idx: number) {
  if (e.key === "Escape") {
    e.preventDefault()
    e.stopPropagation()
    closeList(true)
    return
  }

  if (e.key === "Tab") {
    e.preventDefault()
    const delta = e.shiftKey ? -1 : 1
    focusOptionIndex(moveIndexCircular(idx, delta))
    return
  }

  if (e.key === "ArrowDown") {
    e.preventDefault()
    focusOptionIndex(moveIndexCircular(idx, 1))
    return
  }

  if (e.key === "ArrowUp") {
    e.preventDefault()
    focusOptionIndex(moveIndexCircular(idx, -1))
    return
  }

  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault()
    const opt = props.options[idx]
    if (opt !== undefined) {
      pickOption(opt.value)
    }
  }
}

function onDocPointerDown(ev: MouseEvent | PointerEvent) {
  const t = ev.target as Node | null
  if (!t || !rootRef.value?.contains(t)) {
    closeList(false)
  }
}

onMounted(() => {
  syncPanelWidth()
  window.addEventListener("resize", syncPanelWidth)
  document.addEventListener("mousedown", onDocPointerDown)
})
onUnmounted(() => {
  window.removeEventListener("resize", syncPanelWidth)
  document.removeEventListener("mousedown", onDocPointerDown)
})
</script>
 